import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createCallbackRequest, getCallbackRequests } from '../services/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Phone, Check, Clock, User, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useEffect } from 'react';

const CallbackRequestPage = () => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [previousRequests, setPreviousRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPreviousRequests();
  }, []);

  const fetchPreviousRequests = async () => {
    try {
      const response = await getCallbackRequests();
      setPreviousRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createCallbackRequest({ message: message || null });
      toast.success('Demande envoyée ! Votre expert vous contactera bientôt.');
      setSubmitted(true);
      setMessage('');
      fetchPreviousRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#F9C80E]/10 text-[#F9C80E]">
            <Clock className="w-3 h-3" /> En attente
          </span>
        );
      case 'contacted':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#397DFF]/10 text-[#397DFF]">
            <Phone className="w-3 h-3" /> Contacté
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#10D183]/10 text-[#10D183]">
            <Check className="w-3 h-3" /> Terminé
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="callback-request-page">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-[#397DFF]/10 flex items-center justify-center mx-auto mb-4">
          <Phone className="w-8 h-8 text-[#397DFF]" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#191D1E]">
          Demander un rappel
        </h1>
        <p className="text-[#687175] mt-2">
          Votre expert énergie vous contactera dans les plus brefs délais
        </p>
      </div>

      {/* Form */}
      {!submitted ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-6 hover:shadow-md transition-shadow duration-200"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#191D1E]">Vos coordonnées</Label>
              <div className="mt-2 p-4 bg-[#F5F6F9] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-[#BFC6CC]/30">
                    <User className="w-5 h-5 text-[#687175]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#191D1E]">{user?.name}</p>
                    <p className="text-sm text-[#687175]">{user?.email}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="message" className="text-[#191D1E]">
                Message (optionnel)
              </Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E] placeholder-[#939BA0] focus:border-[#397DFF] min-h-[120px]"
                placeholder="Décrivez votre besoin ou vos questions..."
                data-testid="callback-message-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={submitting}
              data-testid="submit-callback-btn"
            >
              {submitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </Button>
          </form>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-8 text-center hover:shadow-md transition-shadow duration-200"
        >
          <div className="w-16 h-16 rounded-full bg-[#10D183]/10 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[#10D183]" />
          </div>
          <h2 className="text-xl font-bold text-[#191D1E] mb-2">
            Demande envoyée !
          </h2>
          <p className="text-[#687175] mb-4">
            Votre expert énergie vous contactera très bientôt.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            className="btn-secondary"
            data-testid="new-callback-btn"
          >
            Nouvelle demande
          </Button>
        </motion.div>
      )}

      {/* Previous Requests */}
      {previousRequests.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-[#191D1E] mb-4">
            Mes demandes précédentes
          </h2>
          <div className="space-y-3">
            {previousRequests.map((request) => (
              <div 
                key={request.id}
                className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-4 flex items-center justify-between hover:shadow-md transition-shadow duration-200"
                data-testid={`previous-request-${request.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F5F6F9] flex items-center justify-center">
                    {request.message ? (
                      <MessageSquare className="w-5 h-5 text-[#687175]" />
                    ) : (
                      <Phone className="w-5 h-5 text-[#687175]" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-[#191D1E]">
                      {request.message || 'Demande de rappel'}
                    </p>
                    <p className="text-xs text-[#939BA0]">
                      {new Date(request.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                {getStatusBadge(request.status)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallbackRequestPage;
